import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export const Header = () => {
  const lenis = useLenis();
  const { t, lang, setLang, languages } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (id) => lenis?.scrollTo(`#${id}`, { offset: 0, duration: 1.4 });
  const links = [
    { label: t.nav.work, id: "work" },
    { label: t.nav.studio, id: "studio" },
    { label: t.nav.contact, id: "contact" },
  ];

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass py-4" : "py-6 bg-transparent"}`}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.33, 1, 0.68, 1] }}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-6 flex items-center justify-between">
        <button
          onClick={() => lenis?.scrollTo(0, { duration: 1.2 })}
          className="font-anton text-2xl tracking-tightest text-white hover:opacity-70 transition-opacity"
          data-testid="brand-logo"
        >
          ONYQX
        </button>
        <nav className="flex items-center gap-6 sm:gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="hidden sm:block text-sm font-medium text-white/70 hover:text-white transition-colors relative group"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-white transition-all duration-300 group-hover:w-full" />
            </button>
          ))}

          {/* Language switcher (custom dropdown, no scroll-lock, EN default) */}
          <div className="relative" ref={langRef} data-testid="language-switcher">
            <button
              onClick={() => setLangOpen((o) => !o)}
              className="flex items-center gap-1 rounded-full border border-white/15 bg-black/40 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-black/60"
              data-testid="language-trigger"
              aria-haspopup="listbox"
              aria-expanded={langOpen}
            >
              {languages.find((l) => l.code === lang)?.label || "EN"}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div
                className="absolute right-0 mt-2 w-24 overflow-hidden rounded-xl border border-white/10 bg-[#0b0b0b] py-1 shadow-2xl"
                data-testid="language-menu"
                role="listbox"
              >
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    className={`block w-full px-4 py-2 text-left text-xs font-semibold transition-colors ${
                      lang === l.code ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"
                    }`}
                    data-testid={`lang-${l.code}`}
                    role="option"
                    aria-selected={lang === l.code}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => go("contact")}
            className="hidden rounded-full bg-white px-5 py-2 text-sm font-semibold text-black hover:bg-white/80 transition-colors sm:block"
            data-testid="header-cta"
          >
            {t.nav.cta}
          </button>
        </nav>
      </div>
    </motion.header>
  );
};
